import React, { useRef } from "react";
import { IconButton, Slider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

const SIZE_TO_VALUE = { small: 0, medium: 50, large: 100 };
const VALUE_TO_SIZE = { 0: "small", 50: "medium", 100: "large" };

const LogoUpload = ({
  title,
  value,
  previewValue,
  defaultValue,
  name,
  onChange,
  recommendation,
  size = "large",
  hideUploadButton = false,
  onRemove,
  onGhlPick,
  showSizeOption = false,
  sizeOptionValue,
  onSizeOptionChange,
}) => {
  const hasImage = value || previewValue;
  const hasSavedImage = !!value;
  const fileInputRef = useRef(null);

  const previewBoxStyles = {
    large: {
      minWidth: { xs: "120px", sm: "140px", md: "160px", lg: "200px", xl: "240px" },
      minHeight: { xs: "60px", sm: "70px", md: "80px", lg: "100px", xl: "120px" },
      height: { xs: "60px", sm: "70px", md: "80px", lg: "100px", xl: "120px" },
      maxWidth: { xs: "100%", sm: "140px", md: "160px", lg: "200px", xl: "240px" }
    },
    small: {
      minWidth: { xs: "96px", sm: "112px", md: "128px", lg: "160px", xl: "192px" },
      minHeight: { xs: "60px", sm: "70px", md: "80px", lg: "100px", xl: "120px" },
      height: { xs: "60px", sm: "70px", md: "80px", lg: "100px", xl: "120px" },
      maxWidth: { xs: "100%", sm: "112px", md: "128px", lg: "160px", xl: "192px" }
    }
  };

  const boxStyles = previewBoxStyles[size];

  return (
    <SoftBox
      sx={{
        width: { xs: "100%", sm: "calc(50% - 6px)" },
        display: "flex",
        flexDirection: "column",
        marginBottom: { xs: 1, md: 0 },
        maxWidth: { xs: "100%", md: "none" }
      }}
    >
      <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
        <SoftTypography
          component="label"
          variant="caption"
          fontWeight="bold"
          textTransform="capitalize"
        >
          {title}
        </SoftTypography>
      </SoftBox>
      <SoftBox
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          gap: { xs: 1, md: 1.5 }
        }}
      >
        <SoftBox
          onClick={() => {
            if (onGhlPick) {
              onGhlPick();
              return;
            }
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
          sx={{
            border: "1.5px solid #e0e0e0",
            borderRadius: "12px",
            padding: { xs: "12px", sm: "14px", md: "16px", lg: "20px", xl: "24px" },
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...boxStyles,
            width: { xs: "100%", md: "auto" },
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease",
            cursor: "pointer",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
              borderColor: "#d0d0d0"
            }
          }}
        >
          <input
            type="hidden"
            name={name}
            value={value || ""}
            defaultValue={defaultValue}
          />
          {hasSavedImage && onRemove && (
            <IconButton
              size="small"
              aria-label="Remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 1,
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                color: "white",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.75)" },
                width: 24,
                height: 24
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {hasImage ? (
            <img
              src={previewValue || value || defaultValue}
              alt={title}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
                borderRadius: "4px"
              }}
            />
          ) : null}
          {!onGhlPick && (
            <input
              ref={fileInputRef}
              hidden
              accept="image/*"
              type="file"
              onChange={(e) => {
                if (onChange) {
                  onChange(e);
                }
                e.target.value = "";
              }}
            />
          )}
        </SoftBox>
        {!hasImage && !hideUploadButton && !onGhlPick && (
          <SoftButton
            className="img-center"
            variant="contained"
            color="primary"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
            sx={{
              minWidth: "120px",
              padding: "12px 20px",
              alignSelf: { xs: "stretch", md: "center" },
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "14px",
              textTransform: "none",
              backgroundColor: "#1976d2",
              boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                backgroundColor: "#1565c0",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)"
              },
              transition: "all 0.2s ease"
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginRight: "8px" }}
            >
              <path
                d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z"
                fill="white"
              />
            </svg>
            Upload Image
          </SoftButton>
        )}
      </SoftBox>
      {recommendation && (
        <SoftTypography
          variant="caption"
          sx={{
            fontSize: "11px",
            color: "#666",
            marginTop: "6px"
          }}
        >
          {recommendation}
        </SoftTypography>
      )}
      {showSizeOption && (
        <SoftBox
          sx={{
            mt: 1.5,
            px: 2.5,
            py: 1,
            borderRadius: "8px",
            backgroundColor: "#fafafa",
            border: "1px solid #eee"
          }}
        >
          <SoftBox
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5
            }}
          >
            <SoftTypography
              variant="caption"
              sx={{ fontSize: "11px", color: "#666", fontWeight: 600 }}
            >
              Logo size
            </SoftTypography>
            <SoftTypography
              variant="caption"
              sx={{
                fontSize: "11px",
                color: "#1976d2",
                fontWeight: 700,
                textTransform: "capitalize"
              }}
            >
              {sizeOptionValue || "medium"}
            </SoftTypography>
          </SoftBox>
          <Slider
            value={SIZE_TO_VALUE[sizeOptionValue] ?? 50}
            step={null}
            marks={[
              { value: 0, label: "Small" },
              { value: 50, label: "Medium" },
              { value: 100, label: "Large" }
            ]}
            onChange={(_, v) => {
              const next = VALUE_TO_SIZE[v];
              if (next && next !== sizeOptionValue && onSizeOptionChange) {
                onSizeOptionChange(next);
              }
            }}
            sx={{
              color: "#1976d2",
              height: 6,
              padding: "13px 0",
              "& .MuiSlider-rail": { opacity: 0.32, height: 6 },
              "& .MuiSlider-track": { height: 6, border: "none" },
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
                backgroundColor: "#1976d2",
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 6px rgba(25, 118, 210, 0.16)"
                }
              },
              "& .MuiSlider-mark": {
                backgroundColor: "#bbb",
                height: 10,
                width: 2,
                borderRadius: 1
              },
              "& .MuiSlider-markActive": { backgroundColor: "#1976d2" },
              "& .MuiSlider-markLabel": {
                fontSize: "10px",
                color: "#666",
                top: 24
              }
            }}
          />
        </SoftBox>
      )}
    </SoftBox>
  );
};

export default LogoUpload;
